import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  CalendarDays, Clock, Users, ShoppingCart, Check,
  Loader2, Plus, Share2, Trash2, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const MyMealsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/meals`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMeals(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [token]);

  const deleteMeal = async (mealId) => {
    try {
      await axios.delete(`${API}/meals/${mealId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMeals((prev) => prev.filter((m) => m.id !== mealId));
      toast.success("Repas supprimé");
    } catch {
      toast.error("Erreur");
    }
  };

  const shareMeal = async (meal) => {
    const url = `${window.location.origin}/meals/${meal.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: meal.title,
          text: `${meal.host_name} t'invite au repas "${meal.title}" !`,
          url,
        });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié !");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const upcoming = meals.filter((m) => new Date(m.meal_date) >= new Date());
  const past = meals.filter((m) => new Date(m.meal_date) < new Date());

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="my-meals-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-charcoal">Mes Repas</h1>
            <p className="text-sm text-muted-foreground">{meals.length} repas au total</p>
          </div>
          <Button onClick={() => navigate("/meals/new")} className="rounded-full gap-2" data-testid="new-meal-btn">
            <Plus className="w-4 h-4" /> Nouveau
          </Button>
        </div>

        {/* Upcoming */}
        {upcoming.length > 0 && (
          <section>
            <h2 className="font-heading text-lg font-semibold text-charcoal mb-3 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-primary" /> A venir
              <Badge>{upcoming.length}</Badge>
            </h2>
            <div className="space-y-3">
              {upcoming.map((meal, i) => (
                <MealCard
                  key={meal.id}
                  meal={meal}
                  isHost={meal.host_id === user?.id}
                  onShare={() => shareMeal(meal)}
                  onDelete={() => deleteMeal(meal.id)}
                  index={i}
                />
              ))}
            </div>
          </section>
        )}

        {/* Past */}
        {past.length > 0 && (
          <section>
            <h2 className="font-heading text-lg font-semibold text-charcoal mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" /> Passés
              <Badge variant="outline">{past.length}</Badge>
            </h2>
            <div className="space-y-3">
              {past.map((meal, i) => (
                <MealCard
                  key={meal.id}
                  meal={meal}
                  isHost={meal.host_id === user?.id}
                  onShare={() => shareMeal(meal)}
                  onDelete={() => deleteMeal(meal.id)}
                  isPast
                  index={i}
                />
              ))}
            </div>
          </section>
        )}

        {meals.length === 0 && (
          <div className="text-center py-16">
            <CalendarDays className="w-14 h-14 mx-auto text-muted-foreground/20 mb-4" />
            <p className="text-muted-foreground mb-4">Aucun repas planifié</p>
            <Button onClick={() => navigate("/meals/new")} className="rounded-full gap-2">
              <Plus className="w-4 h-4" /> Organiser mon premier repas
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const MealCard = ({ meal, isHost, onShare, onDelete, isPast, index }) => {
  const claimedCount = meal.shopping_list?.filter((i) => i.claimed_by).length || 0;
  const totalItems = meal.shopping_list?.length || 0;
  const guestCount = (meal.invited_friend_ids?.length || 0) + 1;
  const dateStr = new Date(meal.meal_date).toLocaleDateString("fr-FR", {
    weekday: "short", day: "numeric", month: "short",
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className={`overflow-hidden ${isPast ? "opacity-70" : ""}`}>
        <CardContent className="p-0">
          <Link to={`/meals/${meal.id}`} className="block p-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
                isPast ? "bg-gray-100" : "bg-primary/10"
              }`}>
                <p className={`text-xs font-bold ${isPast ? "text-gray-500" : "text-primary"}`}>
                  {dateStr.split(" ")[1]}
                </p>
                <p className={`text-[10px] uppercase ${isPast ? "text-gray-400" : "text-primary/70"}`}>
                  {dateStr.split(" ")[2]}
                </p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-charcoal truncate">{meal.title}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                  {meal.meal_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {meal.meal_time}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" /> {guestCount}
                  </span>
                  {totalItems > 0 && (
                    <span className="flex items-center gap-1">
                      <ShoppingCart className="w-3 h-3" /> {claimedCount}/{totalItems}
                    </span>
                  )}
                </div>
                {meal.recipe_name && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <GustoIcon className="w-4 h-4" /> {meal.recipe_name}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                {isHost && (
                  <Badge className="text-[10px] bg-primary/10 text-primary">Hôte</Badge>
                )}
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </Link>
          {/* Actions */}
          <div className="flex border-t divide-x">
            <button
              onClick={onShare}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs text-muted-foreground hover:bg-gray-50 transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" /> Partager
            </button>
            {isHost && (
              <button
                onClick={onDelete}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs text-red-400 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MyMealsPage;
